import { Button, DatePicker, Form, Input, Modal, Typography, message } from 'antd';
import { Dayjs } from 'dayjs';

import { useAdminBillingDocumentController_createOneTimeInvoice } from '@api-client/generated/AdminBillingDocumentController/createOneTimeInvoice';
import { ScrollbarBase } from '@components';
import { InvoiceItemsFormList } from '@entities/billing/modals/InvoiceItemsFormList/InvoiceItemsFormList';
import { useCurrentCompanyId } from '@hooks';
import { getApiErrorMessage } from '@utils';

import * as S from './styled';

type FormValues = {
  number: string;
  issueDate: Dayjs;
  dueDate: Dayjs;
  items: {
    name: string;
    description: string;
    unitPrice: number;
    totalPrice: number;
    taxRate: number;
  }[];
};

type ModalBillingOneTimeInvoiceProps = {
  isOpen: boolean;
  onCancel: () => void;
  onAfterCreate: () => void;
};

const ModalBillingOneTimeInvoice = (props: ModalBillingOneTimeInvoiceProps) => {
  const [form] = Form.useForm<FormValues>();
  const companyId = useCurrentCompanyId();

  const { mutate: createInvoice, isPending: isCreateInvoicePending } =
    useAdminBillingDocumentController_createOneTimeInvoice();

  const handleSubmit = (values: FormValues) => {
    createInvoice(
      {
        parameter: {
          companyId: companyId,
        },
        requestBody: {
          documentMetadata: {
            amount: null,
            currencyCode: null,
            issueDate: values.issueDate.format('YYYY-MM-DD'),
            dueDate: values.dueDate.format('YYYY-MM-DD'),
            number: values.number,
            items: values.items.map((item) => ({
              name: item.name,
              description: item.description,
              amount: null,
              taxRate: item.taxRate,
              unitPrice: item.unitPrice,
              totalPrice: item.totalPrice,
            })),
          },
        },
      },
      {
        onSuccess: () => {
          props.onAfterCreate();
          form.resetFields();
          props.onCancel();
        },
        onError: (error) => {
          message.error(getApiErrorMessage(error, 'Failed to create the invoice'));
        },
      }
    );
  };

  return (
    <Modal
      open={props.isOpen}
      title="One time invoice"
      // Wide enough for "Price before VAT" and the five VAT-rate buttons of a
      // line item to share a row without cramping either.
      width={640}
      footer={null}
      onCancel={props.onCancel}
      destroyOnClose
      centered
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ items: [{}] }}
        onFinish={handleSubmit}
      >
        {/* An invoice can carry any number of line items; scroll the fields so a
            long list never pushes the buttons past the bottom of the viewport.
            200px covers the modal's own header and footer (~170px) and leaves a
            little margin, so a single-item invoice still fits without scrolling. */}
        <ScrollbarBase height="auto" maxHeight="calc(100vh - 200px)">
          <Form.Item
            label="Invoice number"
            name="number"
            rules={[{ required: true }]}
          >
            <Input size="large" />
          </Form.Item>
          <S.DateRow gap={16} data-testid={S.DATE_ROW_TESTID}>
            <Form.Item
              label="Issued date"
              name="issueDate"
              rules={[{ required: true }]}
            >
              <DatePicker size="large" />
            </Form.Item>
            <Form.Item
              label="Due date"
              name="dueDate"
              rules={[{ required: true }]}
            >
              <DatePicker size="large" />
            </Form.Item>
          </S.DateRow>
          <Typography.Title level={3}>Items</Typography.Title>
          <InvoiceItemsFormList itemNoun="Item" />
        </ScrollbarBase>
        <S.Footer gap={16} justify="flex-end">
          <Button onClick={props.onCancel} size="large">
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            loading={isCreateInvoicePending}
          >
            Submit
          </Button>
        </S.Footer>
      </Form>
    </Modal>
  );
};

export default ModalBillingOneTimeInvoice;
